import type { LaneColour, LaneStatus } from "./types";

// War Room v0.5 — optional runtime fields layered onto static lane records.
// Server is authoritative for `colour`; client never computes it.
export type LaneListEntry = {
  id: string;
  name: string;
  emoji: string;
  avatar_url: string;
  signoff?: string;
  status?: LaneStatus;
  colour?: LaneColour;
  ageMinutes?: number;
  lastOutputExcerpt?: string | null;
};

// Roster source of truth (24 desks). PNG art exists for the first 13; the rest
// fall back to a generated SVG disc with the desk emoji.
type RosterRow = { id: string; name: string; emoji: string; signoff: string; art: "png" | "svg" };

const ROSTER: RosterRow[] = [
  // Cross — CoS / Partners with photo art
  { id: "tess",    name: "Tess",    emoji: "🧭", signoff: "One thing at a time.", art: "png" },
  { id: "frankie", name: "Frankie", emoji: "🦾", signoff: "Miss me already?",     art: "png" },
  { id: "hart",    name: "Hart",    emoji: "🏔️", signoff: "What does this cost?", art: "png" },
  { id: "sage",    name: "Sage",    emoji: "🌿", signoff: "Make it obvious.",      art: "png" },
  { id: "knox",    name: "Knox",    emoji: "🏗️", signoff: "Next.",                 art: "png" },
  { id: "benny",   name: "Benny",   emoji: "📬", signoff: "Nothing drops.",        art: "png" },
  // VeyraPay
  { id: "ash",     name: "Ash",     emoji: "⚡", signoff: "Zero downtime.",        art: "png" },
  { id: "rex",     name: "Rex",     emoji: "🔩", signoff: "Again.",                art: "png" },
  { id: "ralph",   name: "Ralph",   emoji: "📡", signoff: "No excuses.",           art: "png" },
  { id: "vex",     name: "Vex",     emoji: "🦊", signoff: "On to the next.",       art: "png" },
  { id: "forge",   name: "Forge",   emoji: "🔨", signoff: "Built to spec.",        art: "png" },
  // Peak
  { id: "dingo",   name: "Dingo",   emoji: "🐕", signoff: "No drama.",             art: "png" },
  { id: "milo",    name: "Milo",    emoji: "🎯", signoff: "Feed the winners.",     art: "png" },
  // Cross — generated SVG (no photo art yet)
  { id: "doc",     name: "Doc",     emoji: "🩺", signoff: "Vitals first.",         art: "svg" },
  { id: "anvil",   name: "Anvil",   emoji: "⚙️", signoff: "Hold the line.",        art: "svg" },
  { id: "atlas",   name: "Atlas",   emoji: "🗺️", signoff: "Map the terrain.",      art: "svg" },
  { id: "halo",    name: "Halo",    emoji: "🛡️", signoff: "Above the line.",       art: "svg" },
  { id: "counsel", name: "Counsel", emoji: "📜", signoff: "On the record.",        art: "svg" },
  { id: "hitman",  name: "Hitman",  emoji: "🎯", signoff: "Clean shot.",           art: "svg" },
  { id: "reno",    name: "Reno",    emoji: "🔧", signoff: "Bring it back.",        art: "svg" },
  // Skinwire
  { id: "velvet",  name: "Velvet",  emoji: "🌹", signoff: "Stay in character.",    art: "svg" },
  { id: "studio",  name: "Studio",  emoji: "🎬", signoff: "Roll camera.",          art: "svg" },
  { id: "glitter", name: "Glitter", emoji: "🪩", signoff: "Eyes on us.",           art: "svg" },
  { id: "coco",    name: "Coco",    emoji: "💋", signoff: "Keep them hooked.",     art: "svg" },
];

export function avatarUrlFor(id: string): string {
  const row = ROSTER.find((r) => r.id === id.toLowerCase());
  if (row) return `/avatars/${row.id}.${row.art}`;
  return `/avatars/${id.toLowerCase()}.png`;
}

export function emojiFor(id: string): string {
  return ROSTER.find((r) => r.id === id.toLowerCase())?.emoji ?? "";
}

// Inline SVG data URL fallback used by <img onError> when the asset is missing.
export function emojiDataUrl(emoji: string, ring = "#7cc9ff", bg = "#13161b"): string {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128'>` +
    `<circle cx='64' cy='64' r='62' fill='${bg}' stroke='${ring}' stroke-width='2'/>` +
    `<text x='64' y='64' text-anchor='middle' dominant-baseline='central' font-size='64' ` +
    `font-family='Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'>${emoji}</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Static fallback used before /api/state hydrates.
export const LANE_LIST: LaneListEntry[] = ROSTER.map((r) => ({
  id: r.id,
  name: r.name,
  emoji: r.emoji,
  avatar_url: `/avatars/${r.id}.${r.art}`,
  signoff: r.signoff,
}));

export const STAGE_ORDER = ["knox", "sage", "rex", "ash", "live"] as const;
export const STAGE_LABEL: Record<string, string> = {
  knox: "Knox · Entity",
  sage: "Sage · Shield",
  rex: "Rex · Gate",
  ash: "Ash · Sync",
  live: "Live",
};

export const STATUS_DOT: Record<string, string> = {
  idle: "bg-mist",
  working: "bg-glow",
  awaiting: "bg-warn",
  blocked: "bg-bad",
  error: "bg-bad",
};

// Brand mapping by desk id — used by brand-filtered tabs (Veyra, Peak) and the
// Metrics tab. Kept here so a single change updates every brand-filtered view.
// Source of truth: desk-governance brand-lead table.
export type Brand = "veyrapay" | "peak" | "skinwire" | "labs" | "cross";

const BRAND_BY_LANE: Record<string, Brand> = {
  // VeyraPay
  ash: "veyrapay", ralph: "veyrapay", vex: "veyrapay", forge: "veyrapay",
  // Peak Peptides
  dingo: "peak", milo: "peak",
  // Skinwire
  velvet: "skinwire", studio: "skinwire", glitter: "skinwire", coco: "skinwire",
  // Veyra Labs
  atlas: "labs",
};

export function laneBrand(id: string): Brand {
  return BRAND_BY_LANE[id.toLowerCase()] ?? "cross";
}
